
import React, { useEffect, useState } from 'react';
import { useStandUp } from '../contexts/StandUpContext';
import { 
    X, 
    Settings, 
    Trash2, 
    Loader2, 
    Check, 
    Plug, 
    Search, 
    AlertTriangle,
    Shield,
    Globe,
    Lock
} from 'lucide-react';
import AlertModal from './AlertModal';

const TeamSettingsModal: React.FC = () => {
    const {
        isTeamSettingsModalOpen,
        closeTeamSettingsModal,
        viewingTeamSettingsId,
        userProfile,
        showToast,
        availableProviders,
        deleteTeam,
        getTeam,
        updateTeam,
        isTeamNameDuplicate,
        updateTeamProjectMappings,
        getExternalProjectList
    } = useStandUp();

    const [teamName, setTeamName] = useState('');
    const [teamOwnerId, setTeamOwnerId] = useState<string | null>(null);
    const [teamVisibility, setTeamVisibility] = useState<'public' | 'protected' | 'private'>('public');
    const [isSavingName, setIsSavingName] = useState(false);
    const [isSavingVisibility, setIsSavingVisibility] = useState(false);
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);

    // Handle Escape Key
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                closeTeamSettingsModal();
            }
        };
        if (isTeamSettingsModalOpen) window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isTeamSettingsModalOpen, closeTeamSettingsModal]);

    // Integration State
    const [selectedProvider, setSelectedProvider] = useState<string>('');
    const [selectedProviderProjectIds, setSelectedProviderProjectIds] = useState<string[]>([]);
    const [availableProviderProjects, setAvailableProviderProjects] = useState<{id: string, name: string}[]>([]);
    const [isFetchingProjects, setIsFetchingProjects] = useState(false);
    const [isSavingProjects, setIsSavingProjects] = useState(false);
    const [projectSearch, setProjectSearch] = useState('');
    const [configError, setConfigError] = useState<string | null>(null);

    useEffect(() => {
        if (availableProviders.length > 0 && !selectedProvider) {
            setSelectedProvider(availableProviders[0].id);
        }
    }, [availableProviders, selectedProvider]);

    useEffect(() => {
        if (isTeamSettingsModalOpen && viewingTeamSettingsId) {
            // Reset state
            setConfigError(null);
            
            // 1. Fetch Team Doc
            getTeam(viewingTeamSettingsId).then(data => {
                if(data) {
                    setTeamOwnerId(data.ownerId);
                    setTeamName(data.name); 
                    setTeamVisibility(data.visibility || 'public');
                    
                    // Load existing project IDs for current provider
                    if (selectedProvider) {
                        const providerIds = data.externalProjectIds?.[selectedProvider] || [];
                        setSelectedProviderProjectIds(providerIds);
                    }
                }
            });

            // 2. Fetch Projects if provider selected
            if (selectedProvider) {
                fetchProjects();
            }
        }
    }, [isTeamSettingsModalOpen, viewingTeamSettingsId, selectedProvider]);

    const handleSaveVisibility = async (newVisibility: 'public' | 'protected' | 'private') => {
        if (!viewingTeamSettingsId || isSavingVisibility) return;
        setIsSavingVisibility(true);
        try {
            await updateTeam(viewingTeamSettingsId, {
                visibility: newVisibility
            });
            setTeamVisibility(newVisibility);
            showToast(`Visibility updated to ${newVisibility}`);
        } catch (e) {
            console.error(e);
            showToast("Failed to update visibility", "error");
        } finally {
            setIsSavingVisibility(false);
        }
    };

    const fetchProjects = async () => {
        setIsFetchingProjects(true);
        setAvailableProviderProjects([]);
        setConfigError(null);

        try {
            const projects = await getExternalProjectList(selectedProvider);
            if (projects) {
                setAvailableProviderProjects(projects);
                if (projects.length === 0) {
                    setConfigError(`No projects found. Ensure your Admin has configured ${selectedProvider} in the Portal.`);
                }
            }
        } catch (e) {
            console.error("External projects fetch error:", e);
            setConfigError(`Connection failed. Ensure ${selectedProvider} credentials are set in the Admin Portal.`);
        } finally {
            setIsFetchingProjects(false);
        }
    };

    const handleSaveName = async () => {
        // NORMALIZATION: Trim outer spaces AND collapse multiple internal spaces into one
        const normalized = teamName.trim().replace(/\s+/g, ' ');
        
        if (!viewingTeamSettingsId || !normalized || !userProfile?.orgId) return;
        setIsSavingName(true);
        try {
            // 1. Check for duplicates in same Org
            const isDuplicate = await isTeamNameDuplicate(userProfile.orgId, normalized);
            
            if (isDuplicate) {
                // Double check it's not THIS team's name
                const currentTeam = await getTeam(viewingTeamSettingsId);
                if (currentTeam?.name !== normalized) {
                    showToast(`A team named "${normalized}" already exists in your organization.`, "error");
                    setIsSavingName(false);
                    return;
                }
            }

            await updateTeam(viewingTeamSettingsId, { name: normalized });
            showToast("Team renamed successfully!");
        } catch (e: any) {
            console.error("Rename error:", e);
            showToast(e.message || "Failed to rename team", "error");
        } finally {
            setIsSavingName(false);
        }
    };

    const handleToggleProject = (id: string) => {
        setSelectedProviderProjectIds(prev => 
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const handleSaveProjects = async () => {
        if (!viewingTeamSettingsId) return;
        setIsSavingProjects(true);
        try {
            await updateTeamProjectMappings(
                viewingTeamSettingsId,
                selectedProvider,
                selectedProviderProjectIds,
                availableProviderProjects,
                userProfile?.orgId
            );
            showToast(`${selectedProvider} mappings updated!`);
        } catch (e: any) {
            showToast("Failed to save projects: " + e.message, "error");
        } finally { setIsSavingProjects(false); }
    };

    const handleDeleteTeam = async () => {
        if (!viewingTeamSettingsId) return;
        try {
            await deleteTeam(viewingTeamSettingsId);
            setShowDeleteAlert(false); // Reset the state!
            closeTeamSettingsModal();
            showToast("Team deleted successfully");
        } catch (e) {
            showToast("Failed to delete team", "error");
        }
    };

    if (!isTeamSettingsModalOpen || !viewingTeamSettingsId) return null;

    const isOwner = userProfile?.id === teamOwnerId;

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-gray-800 w-full max-w-md rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col max-h-[90vh] overflow-hidden">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900/50">
                    <div className="flex items-center gap-2">
                        <Settings size={18} className="text-gray-400" />
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Team Settings</h2>
                    </div>
                    <button onClick={closeTeamSettingsModal} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-200">
                        <X size={20} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-8">
                    {/* General Settings */}
                    <section>
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">General</div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 mb-1.5 px-1">Team Name</label>
                                <div className="flex gap-2">
                                    <input 
                                        type="text"
                                        value={teamName}
                                        onChange={e => setTeamName(e.target.value)}
                                        className="flex-1 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm focus:ring-2 ring-brand-500 outline-none dark:text-white"
                                    />
                                    <button 
                                        onClick={handleSaveName}
                                        disabled={isSavingName || !teamName.trim()}
                                        className="px-4 bg-brand-600 hover:bg-brand-700 text-white text-xs font-bold rounded-lg transition-all disabled:opacity-50"
                                    >
                                        {isSavingName ? <Loader2 size={14} className="animate-spin" /> : "Rename"}
                                    </button>
                                </div>
                            </div>

                            {/* Visibility Settings */}
                            <div className="space-y-3">
                                <label className="block text-xs font-bold text-gray-500 mb-1.5 px-1">Privacy & Access</label>
                                <div className="grid grid-cols-1 gap-2">
                                    <button 
                                        onClick={() => handleSaveVisibility('public')}
                                        disabled={isSavingVisibility}
                                        className={`flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${teamVisibility === 'public' ? 'bg-brand-50 dark:bg-brand-900/20 border-brand-500' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-brand-200'}`}
                                    >
                                        <Globe size={16} className={teamVisibility === 'public' ? 'text-brand-600' : 'text-gray-400'} />
                                        <div className="flex-1">
                                            <p className="text-xs font-bold dark:text-white text-gray-900">Public</p>
                                            <p className="text-[10px] text-gray-500">Anyone in org can search and join.</p>
                                        </div>
                                        {teamVisibility === 'public' && <Check size={14} className="text-brand-600" />}
                                    </button>

                                    <button 
                                        onClick={() => handleSaveVisibility('protected')}
                                        disabled={isSavingVisibility}
                                        className={`flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${teamVisibility === 'protected' ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-500' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-brand-200'}`}
                                    >
                                        <Shield size={16} className={teamVisibility === 'protected' ? 'text-indigo-600' : 'text-gray-400'} />
                                        <div className="flex-1">
                                            <p className="text-xs font-bold dark:text-white text-gray-900">Protected</p>
                                            <p className="text-[10px] text-gray-500">Visible in search, but requires approval.</p>
                                        </div>
                                        {teamVisibility === 'protected' && <Check size={14} className="text-indigo-600" />}
                                    </button>

                                    <button 
                                        onClick={() => handleSaveVisibility('private')}
                                        disabled={isSavingVisibility}
                                        className={`flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${teamVisibility === 'private' ? 'bg-gray-100 dark:bg-gray-700 border-gray-400' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-brand-200'}`}
                                    >
                                        <Lock size={16} className={teamVisibility === 'private' ? 'text-gray-600 dark:text-gray-300' : 'text-gray-400'} />
                                        <div className="flex-1">
                                            <p className="text-xs font-bold dark:text-white text-gray-900">Private</p>
                                            <p className="text-[10px] text-gray-500">Invitation only. Hidden from discovery.</p>
                                        </div>
                                        {teamVisibility === 'private' && <Check size={14} className="text-gray-600 dark:text-gray-300" />}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Integrations Section */}
                    {availableProviders.length > 0 && (
                        <section>
                            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                <Plug size={12} /> External Integrations
                            </div>
                            <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-xl p-4 space-y-4">
                                <div>
                                    <label className="text-[10px] font-bold text-gray-500 uppercase">Select Service</label>
                                    <div className="flex gap-2 mt-1.5">
                                        {availableProviders.map(p => (
                                            <button
                                                key={p.id}
                                                onClick={() => setSelectedProvider(p.id)}
                                                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                                    selectedProvider === p.id 
                                                    ? 'bg-brand-500 text-white shadow-md' 
                                                    : 'bg-white dark:bg-gray-800 text-gray-500 border border-gray-200 dark:border-gray-700 hover:border-brand-300'
                                                }`}
                                            >
                                                {p.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="w-full h-px bg-gray-200 dark:bg-gray-700"></div>

                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="text-[10px] font-bold text-gray-500 uppercase">Link {availableProviders.find(p => p.id === selectedProvider)?.name} Projects</label>
                                        {isFetchingProjects && <Loader2 size={12} className="animate-spin text-brand-500" />}
                                    </div>
                                    
                                    {configError ? (
                                        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded-lg text-[10px] text-amber-700 dark:text-amber-300 leading-relaxed italic">
                                            {configError}
                                        </div>
                                    ) : (
                                        <>
                                            <div className="relative mb-3">
                                                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                                <input 
                                                    type="text"
                                                    placeholder={`Search projects...`}
                                                    value={projectSearch}
                                                    onChange={(e) => setProjectSearch(e.target.value)}
                                                    className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-xs focus:ring-2 ring-brand-500 outline-none dark:text-white"
                                                />
                                            </div>

                                            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-1 custom-scrollbar">
                                                {availableProviderProjects.length === 0 && !isFetchingProjects && (
                                                    <p className="text-[10px] text-gray-400 italic">No projects found.</p>
                                                )}
                                                {availableProviderProjects
                                                    .filter(p => p.name.toLowerCase().includes(projectSearch.toLowerCase()))
                                                    .map(project => {
                                                        const isSelected = selectedProviderProjectIds.includes(project.id);
                                                        return (
                                                            <button
                                                                key={project.id}
                                                                onClick={() => handleToggleProject(project.id)}
                                                                className={`px-2 py-1 rounded-md text-[10px] font-medium border transition-all ${
                                                                    isSelected 
                                                                    ? 'bg-brand-500 border-brand-600 text-white shadow-sm' 
                                                                    : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-brand-300'
                                                                }`}
                                                            >
                                                                {project.name}
                                                            </button>
                                                        );
                                                    })}
                                            </div>
                                        </>
                                    )}
                                </div>

                                <button 
                                    onClick={handleSaveProjects}
                                    disabled={isSavingProjects || isFetchingProjects || !!configError}
                                    className="w-full py-2 bg-brand-600 hover:bg-brand-700 text-white text-xs font-bold rounded-lg disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                                >
                                    {isSavingProjects ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                                    Save Mappings
                                </button>
                            </div>
                        </section>
                    )}

                    {/* Danger Zone */}
                    {isOwner && (
                        <section className="pt-4 border-t border-gray-100 dark:border-gray-700">
                            <div className="bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-xl p-4 flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-bold text-red-700 dark:text-red-400">Delete this team</p>
                                    <p className="text-[10px] text-red-600/70 dark:text-red-400/50">Permanently remove this team and all its tasks.</p>
                                </div>
                                <button 
                                    onClick={() => setShowDeleteAlert(true)}
                                    className="p-2 bg-white dark:bg-gray-800 border border-red-200 dark:border-red-900/30 rounded-lg text-red-600 hover:bg-red-600 hover:text-white transition-all shadow-sm"
                                >
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        </section>
                    )}
                </div>
            </div>

            <AlertModal 
                isOpen={showDeleteAlert}
                title="Delete Team"
                message={`Are you sure you want to delete "${teamName}"? This will permanently remove all tasks and member associations. This cannot be undone.`}
                confirmText="Delete Team"
                isDestructive={true}
                onConfirm={handleDeleteTeam}
                onCancel={() => setShowDeleteAlert(false)}
            />
        </div>
    );
};

export default TeamSettingsModal;
